import type {
  ApprovalDecision,
  ApprovalDetail,
  DocumentView,
  PoolCheck,
} from '@myrewards/contracts';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import {
  DropZone,
  FileTrigger,
  Link,
  RadioButton,
  RadioField,
  RadioGroup,
} from 'react-aria-components';
import { useNavigate, useParams } from 'react-router';
import {
  approvalQuery,
  approvalsQuery,
  decide,
  documentsQuery,
  uploadDocument,
} from '../../api/ops';
import { meQuery } from '../../api';
import { budgetsForYearQuery } from '../../api/settings';
import { useStacked } from '../../awards/entry/useStacked';
import { PoolEditor } from '../../settings/BudgetPoolsPage';
import { Hero, PageBody, TwoColumns } from '../../shell';
import {
  Button,
  ButtonLink,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  formatDate,
  formatMoney,
  Icon,
  plural,
  scoped,
  StatusPill,
  TextField,
  useToast,
  type ConfirmSummaryRow,
} from '../../ui';
import { amountWords, KIND_LABEL, problemWords } from '../labels';
import { ACCEPTED_FILES, AttachButton, BackLink, FileList, Notice } from '../shared';
import {
  BudgetCard,
  ChargeRuleCard,
  HowItWorksCard,
  includedWords,
  LedgerCard,
  NotesCard,
  PeopleCard,
  SummaryCard,
  ThenArrow,
  TopUpFields,
  TopUpRules,
} from './parts';
import {
  actingHeading,
  actingOffer,
  afterTopUp,
  approvalHeading,
  declineNoteHint,
  demandOf,
  editedTotals,
  isDirty,
  listCard,
  overBudgetPools,
  poolStat,
  sectorPool,
  topUpDrafts,
  topUpPayload,
  type Edits,
  type TopUpDraft,
} from './presenter';
import { ActedConfirmation, type ActedDone } from './ActedConfirmation';
import {
  ExecutiveStep,
  OverageStep,
  RecordStep,
  settleRefusal,
  type ActingRefusal,
  type ExecutiveStepOpen,
  type HeldRefusal,
  type ApprovalOffer,
  type OverageStepOpen,
} from './acting';
import { LeadEntryView, type LeadOffer } from './LeadDecision';
import { ExecutiveBar, filterOf } from './ExecutiveBar';
import { ReviewCard } from './ReviewCard';
import { useApprovalCommand } from './useApprovalCommand';
import s from '../Ops.module.css';

type Decision = ApprovalDecision;

/** Sends a decision; on success says what happened and returns to the list. */
function useDecision(detail: ApprovalDetail, done: (detail: ApprovalDetail) => string) {
  const toast = useToast();
  const navigate = useNavigate();
  return useApprovalCommand(
    detail.award.id,
    (decision: Decision) => decide(detail.award.id, decision),
    (next) => {
      toast.show({ tone: 'done', title: done(next) });
      void navigate('/approvals');
    },
  );
}

/**
 * The top-up drafts, starting again from the server's suggestion whenever it changes (an edit that
 * changes the budget, or someone else's change), and otherwise keeping what the person typed.
 */
function useTopUpDrafts(detail: ApprovalDetail): [TopUpDraft[], (drafts: TopUpDraft[]) => void] {
  const signature = JSON.stringify([detail.suggestedTopUps, detail.budget]);
  const [state, setState] = useState(() => ({ signature, drafts: topUpDrafts(detail) }));
  const drafts = state.signature === signature ? state.drafts : topUpDrafts(detail);
  return [
    drafts,
    (next) => {
      setState({ signature, drafts: next });
    },
  ];
}

function optional(text: string): string | undefined {
  const trimmed = text.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * The other decisions waiting on this person (beside each decision screen). For a Stock Admin
 * acting for an Executive (APP-148), the others waiting on the Executives.
 */
function AlsoWaiting({
  currentId,
  forExecutives = false,
}: {
  currentId: string;
  forExecutives?: boolean;
}): ReactElement {
  const { data } = useQuery(approvalsQuery);
  const others = (data?.items ?? []).filter(
    (d) => d.award.id !== currentId && (!forExecutives || d.actingFor?.role === 'executive'),
  );
  return (
    <Card
      title={`${forExecutives ? 'Also waiting on the Executives' : 'Also waiting'} · ${others.length}`}
      titleSize="sm"
      compact
      help={
        forExecutives
          ? {
              shows: 'Other decisions waiting on the Executives, oldest first.',
              canDo: 'When an Executive asks you to, open the next one and do it for them.',
            }
          : {
              shows: 'Other decisions waiting on you, oldest first.',
              canDo: 'Open one when you’re done here.',
            }
      }
    >
      {others.length === 0 ? (
        <p className={s.muted}>
          {forExecutives
            ? 'Nothing else is waiting on the Executives.'
            : 'Nothing else is waiting on you.'}
        </p>
      ) : (
        <ul className={s.alsoList}>
          {others.slice(0, 5).map((d) => {
            const card = listCard(d);
            return (
              <li key={card.id}>
                <Link href={`/approvals/${card.id}`} className={s.strongLink}>
                  {card.displayId}
                </Link>
                <span className={s.sub}>
                  {d.kind === null ? '' : `${KIND_LABEL[d.kind]} · `}
                  {card.summary}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <ButtonLink href="/approvals" variant="ghost" size="sm">
        All approvals
      </ButtonLink>
    </Card>
  );
}

// --- SL7: a delegate's entry ------------------------------------------------------------------------

function DelegateView({
  detail,
  documents,
}: {
  detail: ApprovalDetail;
  documents: readonly DocumentView[];
}): ReactElement {
  const { award, actions } = detail;
  const creator = award.createdBy.name;
  const over = overBudgetPools(detail).length > 0;
  // A PAA or TAA is the Executives' decision whatever the budget, even one not set yet (APP-070).
  const decidedByExecutives = award.type === 'PAA' || award.type === 'TAA';
  const [dialog, setDialog] = useState<'return' | 'reject' | null>(null);
  const decision = useDecision(detail, (next) =>
    next.award.stage === 'draft'
      ? `${award.displayId ?? 'The entry'} went back to ${creator} with your note`
      : next.award.ended === 'rejected'
        ? `${award.displayId ?? 'The entry'} was rejected; ${creator} is told why`
        : `Approved · ${award.displayId ?? 'the entry'} ${over || decidedByExecutives ? 'goes on to the Executives' : 'goes to the Stock Admin team'}`,
  );
  return (
    <TwoColumns
      sideLabel="More approvals"
      main={
        <>
          <SummaryCard detail={detail} status={{ label: 'Waiting on you', tone: 'waiting' }} />
          <PeopleCard detail={detail} />
          <div className={s.pair}>
            <BudgetCard
              detail={detail}
              note={
                over
                  ? 'This entry goes over budget: approving it sends it on to the Executives to approve a top-up.'
                  : 'If an entry like this ran over budget, approving it would send it on to the Executives.'
              }
            />
            <NotesCard detail={detail} documents={documents} />
          </div>
          <Card
            title="Your decision"
            helpAnchor="approval-decision"
            help={{
              shows: `What happens to ${award.displayId ?? 'this entry'} next is up to you.`,
              canDo: `Approve it, return it to ${creator} with a note to change, or reject it with a reason. ${creator} is told either way.`,
            }}
          >
            <div className={s.actionsRow}>
              {actions.approve && (
                <Button
                  variant="cyan"
                  icon={<Icon name="check" size={18} />}
                  isPending={decision.isPending && decision.variables.action === 'approve'}
                  onPress={() => {
                    decision.mutate({ action: 'approve', version: award.version });
                  }}
                >
                  Approve
                </Button>
              )}
              {actions.return && (
                <Button
                  variant="ghost"
                  onPress={() => {
                    setDialog('return');
                  }}
                >
                  Return with a note
                </Button>
              )}
              {actions.reject && (
                <Button
                  variant="ghost"
                  onPress={() => {
                    setDialog('reject');
                  }}
                >
                  Reject
                </Button>
              )}
            </div>
            <p className={s.muted}>
              {decidedByExecutives
                ? `Approve sends it on to the Executives, who decide it, and ${creator} is told.`
                : over
                  ? `Approve sends it to the Executives for a top-up, and ${creator} is told.`
                  : `Approve sends it to the Stock Admin team, and ${creator} is told.`}
            </p>
            {decision.error !== null && <Notice tone="problem">{decision.error}</Notice>}
          </Card>
          <ConfirmDialog
            isOpen={dialog === 'return'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={`Return ${award.displayId ?? 'this entry'} to ${creator}?`}
            intro={`It goes back to ${creator} as a draft, with your note, to change and send again.`}
            summary={[
              {
                label: 'Entry',
                value: `${award.displayId ?? ''} · ${amountWords(
                  award.calculation.paidFrom.reduce((n, p) => n + p.cashCents, 0),
                  award.calculation.paidFrom.reduce((n, p) => n + p.options, 0),
                )}`,
              },
            ]}
            reasonLabel={`Note to ${creator}`}
            confirmLabel="Return with this note"
            onConfirm={async (note) => {
              await decision
                .mutateAsync({ action: 'return', version: award.version, note })
                .catch(() => undefined);
            }}
          />
          <ConfirmDialog
            isOpen={dialog === 'reject'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={`Reject ${award.displayId ?? 'this entry'}?`}
            intro={`It won’t be paid. ${creator} is told, with your reason.`}
            summary={[{ label: 'Entry', value: award.displayId ?? 'This entry' }]}
            reasonLabel={`Reason for ${creator}`}
            confirmLabel="Reject the entry"
            onConfirm={async (reason) => {
              await decision
                .mutateAsync({ action: 'reject', version: award.version, reason })
                .catch(() => undefined);
            }}
          />
        </>
      }
      side={<AlsoWaiting currentId={award.id} />}
    />
  );
}

/** Under the "for an Executive" buttons (Overage board): what comes next. */
function ActingNote({
  lead = 'You’d be acting for an Executive.',
  next = 'Next you’ll pick the Executive, and you can add files or a note.',
}: {
  lead?: string;
  next?: string;
}): ReactElement {
  return (
    <p className={cx(s.notice, s.actingNote)}>
      <Icon name="people" size={18} />
      <span>
        <span className={s.strong}>{lead}</span> {next} Nothing is saved until you confirm.
      </span>
    </p>
  );
}

/**
 * The Stock Admin is one of the people on an award they could act on for an Executive (OnAward
 * board). Information only: every button stays as it is (no identity rules, D11).
 */
function OnAwardNote({ detail }: { detail: ApprovalDetail }): ReactElement | null {
  const { data: me } = useSuspenseQuery(meQuery);
  const people = detail.lines.filter((l) => l.included);
  if (!people.some((l) => l.personId === me.person.id)) return null;
  return (
    <Notice tone="info">
      <strong>
        {people.length === 1
          ? 'You’re the one person on this award.'
          : `You’re one of the ${plural(people.length, 'person', 'people')} on this award.`}
      </strong>{' '}
      You can still act for an Executive on it. The record, the audit log and the Executive’s notice
      all say who did it and for whom.
    </Notice>
  );
}

// --- Top-up decision (X2, and X3 when over budget) ---------------------------------------------------

function topUpSummary(drafts: readonly TopUpDraft[]): ConfirmSummaryRow[] {
  return drafts.flatMap((d) => {
    const after = afterTopUp(d);
    return [
      {
        label: `${d.pool.name} · top-up`,
        value: amountWords(d.cashCents ?? 0, d.withOptions ? (d.options ?? 0) : 0),
      },
      {
        label: `${d.pool.name} · after`,
        value: amountWords(after.cashCents, d.withOptions ? after.options : 0),
      },
    ];
  });
}

function TopUpCard({
  detail,
  drafts,
  onDrafts,
  approve,
  decline,
  acting = false,
}: {
  detail: ApprovalDetail;
  drafts: readonly TopUpDraft[];
  onDrafts: (drafts: TopUpDraft[]) => void;
  approve?: { label: string; onPress: () => void; isPending: boolean; isDisabled: boolean };
  decline?: { label: string; onPress: () => void };
  /** A Stock Admin's buttons, "for an Executive" (APP-148): the step comes next. */
  acting?: boolean;
}): ReactElement {
  return (
    <Card
      title="Top-up"
      helpAnchor="top-up"
      accent="gold"
      help={{
        shows:
          'How much to add to each over-budget pool for this award. It starts at the amount that brings the pool back to zero.',
        canDo: acting
          ? 'When an Executive asks you to, approve it with the top-up or decline it for them. Keep the top-up, raise it or lower it (zero is allowed); every top-up goes in the pool’s ledger.'
          : 'Keep it, raise it to give room for the year, or lower it (zero is allowed). Every top-up goes in the pool’s ledger.',
      }}
    >
      {drafts.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="pool"
          size="sm"
          headline="No top-up needed"
          body="A budget added since this was sent covers it. Approve it as it is, or decline it."
        />
      ) : (
        <TopUpFields
          drafts={drafts}
          onChange={(index, draft) => {
            onDrafts(drafts.map((d, i) => (i === index ? draft : d)));
          }}
        />
      )}
      {approve && (
        <Button
          variant="cyan"
          block
          isPending={approve.isPending}
          isDisabled={approve.isDisabled}
          onPress={approve.onPress}
        >
          {approve.label}
        </Button>
      )}
      {decline && (
        <Button variant="ghost" block onPress={decline.onPress}>
          {decline.label}
        </Button>
      )}
      {acting && <ActingNote />}
      {acting && <OnAwardNote detail={detail} />}
      <TopUpRules acting={acting} />
      {detail.budget.some((p) => p.status === 'not_set') && (
        <Notice tone="problem">
          A pool on this award has no budget this year. An Executive sets it in Executive › Budget
          pools first.
        </Notice>
      )}
    </Card>
  );
}

function OverageView({
  detail,
  onActed,
  onRefused,
}: {
  detail: ApprovalDetail;
  onActed: (done: ActedDone) => void;
  onRefused: (refusal: ActingRefusal) => void;
}): ReactElement {
  const { award, actions } = detail;
  const creator = award.createdBy.name;
  const { data: me } = useSuspenseQuery(meQuery);
  const [drafts, setDrafts] = useTopUpDrafts(detail);
  const [dialog, setDialog] = useState<'approve' | 'decline' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const decision = useDecision(detail, (next) =>
    next.award.stage === 'draft'
      ? `Declined · ${award.displayId ?? 'the award'} went back to ${creator}`
      : `Approved · ${award.displayId ?? 'the award'} goes to the Stock Admin team`,
  );
  const people = detail.lines.filter((l) => l.included).length;
  // APP-204: who reads a decline's note, when one of them doesn't see the budget.
  const noteHint = declineNoteHint(detail);
  // APP-148: a Stock Admin may do this for a named Executive, never in their own right. Someone
  // who decides it themselves (an Executive) gets their own buttons. One rule, as the hero's.
  const offer = actingOffer(detail);
  const acting =
    offer !== null &&
    offer.role === 'executive' &&
    (offer.actions.approve || offer.actions.decline);
  const [step, setStep] = useState<OverageStepOpen | null>(null);

  const openStep = (kind: 'approve' | 'decline'): void => {
    if (offer === null) return;
    if (kind === 'approve') {
      const payload = topUpPayload(drafts);
      if (!payload.ok) {
        setProblem(payload.message);
        return;
      }
    }
    setProblem(null);
    setStep({ kind, offer });
  };

  return (
    <TwoColumns
      sideLabel="Top-up and decision"
      main={
        <>
          {drafts.length === 0 ? (
            <BudgetCard detail={detail} title="Pools this touches" />
          ) : (
            drafts.map((draft) => <LedgerCard key={draft.pool.id} detail={detail} draft={draft} />)
          )}
          <PeopleCard detail={detail} />
          {decision.error !== null && <Notice tone="problem">{decision.error}</Notice>}
          <ConfirmDialog
            isOpen={dialog === 'approve'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={
              drafts.length === 0
                ? `Approve ${award.displayId ?? 'this award'}?`
                : 'Approve with this top-up?'
            }
            intro={
              drafts.length === 0
                ? 'It goes to the Stock Admin team for the one check.'
                : 'The top-up goes in each pool’s ledger for the year, with your name and reason. Then the award goes to the Stock Admin team.'
            }
            summary={[
              {
                label: 'Award',
                value: `${award.displayId ?? ''} · ${includedWords(people, people)}`,
              },
              ...topUpSummary(drafts),
            ]}
            reasonLabel="Reason (optional)"
            reasonRequired={false}
            confirmLabel={drafts.length === 0 ? 'Approve' : 'Approve with top-up'}
            onConfirm={async (note) => {
              const payload = topUpPayload(drafts);
              if (!payload.ok) return;
              await decision
                .mutateAsync({
                  action: 'approve',
                  version: award.version,
                  topUps: payload.topUps,
                  ...(optional(note) !== undefined && { note: optional(note) }),
                })
                .catch(() => undefined);
            }}
          />
          <ConfirmDialog
            isOpen={dialog === 'decline'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={`Decline ${award.displayId ?? 'this award'}?`}
            intro={`It goes back to ${creator} to reduce. No budget changes.`}
            summary={[{ label: 'Award', value: award.displayId ?? 'This award' }]}
            reasonLabel={`Note to ${creator} (optional)`}
            {...(noteHint !== undefined && { reasonDescription: noteHint })}
            reasonRequired={false}
            confirmLabel="Decline"
            onConfirm={async (reason) => {
              await decision
                .mutateAsync({
                  action: 'decline',
                  version: award.version,
                  ...(optional(reason) !== undefined && { reason: optional(reason) }),
                })
                .catch(() => undefined);
            }}
          />
          {step !== null && (
            <OverageStep
              detail={detail}
              drafts={drafts}
              step={step}
              me={me}
              onActed={onActed}
              onRefused={onRefused}
              onClose={() => {
                setStep(null);
              }}
            />
          )}
        </>
      }
      side={
        <>
          <TopUpCard
            detail={detail}
            drafts={drafts}
            onDrafts={(next) => {
              setProblem(null);
              setDrafts(next);
            }}
            acting={acting}
            {...(actions.approve && {
              approve: {
                label: drafts.length === 0 ? 'Approve' : 'Approve with top-up',
                isPending: decision.isPending,
                isDisabled: detail.budget.some((p) => p.status === 'not_set'),
                onPress: () => {
                  const payload = topUpPayload(drafts);
                  if (!payload.ok) {
                    setProblem(payload.message);
                    return;
                  }
                  setDialog('approve');
                },
              },
            })}
            {...(actions.decline && {
              decline: {
                label: `Decline · back to ${creator.split(' ')[0] ?? creator} to reduce`,
                onPress: () => {
                  setDialog('decline');
                },
              },
            })}
            {...(acting &&
              offer.actions.approve && {
                approve: {
                  label:
                    drafts.length === 0
                      ? 'Approve for an Executive…'
                      : 'Approve with top-up for an Executive…',
                  isPending: false,
                  isDisabled: detail.budget.some((p) => p.status === 'not_set'),
                  onPress: () => {
                    openStep('approve');
                  },
                },
              })}
            {...(acting &&
              offer.actions.decline && {
                decline: {
                  label: 'Decline for an Executive…',
                  onPress: () => {
                    openStep('decline');
                  },
                },
              })}
          />
          {problem !== null && <Notice tone="problem">{problem}</Notice>}
          <AlsoWaiting currentId={award.id} forExecutives={acting} />
        </>
      }
    />
  );
}

// --- X3: a PAA or TAA after the Board's decision -----------------------------------------------------

/**
 * A PAA or TAA can be sent before its pool has the year's budget (APP-070, QA-028). The Executive
 * who approves it sets the budget here, with Budget pools' own editor: the same confirm summary and
 * the same ledger entry, with their name and reason. Then Approve.
 */
function SetBudgetInPlace({
  detail,
  pools,
  onSet,
}: {
  detail: ApprovalDetail;
  pools: readonly PoolCheck[];
  /**
   * The budget was set. This goes away once the approval's own data says so, and the page moves
   * focus on to Approve then.
   */
  onSet: () => void;
}): ReactElement {
  const year = detail.award.fiscalYear ?? null;
  const { data: positions } = useQuery({
    ...budgetsForYearQuery(year ?? 0),
    enabled: year !== null,
  });
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  // Focus never falls to the page (as Budget pools does it, QA-036): into the editor when it opens,
  // back to its Set button on Cancel, and on to Approve once the budget is set. Each move happens in
  // the commit that renders its target, never on a timer, so it can't run ahead of the data.
  const setButtons = useRef(new Map<string, HTMLButtonElement>());
  const editor = useRef<HTMLDivElement>(null);
  const returnTo = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (open !== null) {
      editor.current?.querySelector('input')?.focus();
      return;
    }
    const poolId = returnTo.current;
    returnTo.current = null;
    if (poolId !== null) setButtons.current.get(poolId)?.focus();
  }, [open]);
  const done = (poolId: string): void => {
    // Budget pools' editor refreshes the pool's position before it closes, so the position says
    // whether the budget was set or the editor was cancelled.
    const position =
      year === null
        ? undefined
        : queryClient
            .getQueryData(budgetsForYearQuery(year).queryKey)
            ?.pools.find((x) => x.pool.id === poolId);
    if (position?.budget != null) onSet();
    else returnTo.current = poolId;
    // The budget check on this page follows the new budget; Approve opens up.
    void queryClient.invalidateQueries({ queryKey: approvalQuery(detail.award.id).queryKey });
    setOpen(null);
  };
  return (
    <>
      {pools.map((p) => {
        const name = `${year === null ? '' : `${String(year)} `}${p.pool.name.replace(/\s+pool$/i, '')}`;
        const position = positions?.pools.find((x) => x.pool.id === p.pool.id);
        const floor =
          position === undefined
            ? null
            : amountWords(
                position.inProgress.cashCents + position.paid.cashCents,
                position.inProgress.options + position.paid.options,
              );
        return (
          <div key={p.pool.id} className={s.stack}>
            <Notice tone="waiting">
              The {name} budget isn’t set yet, so this can’t be approved until it is. Set it here:
              it goes in the pool’s ledger with your name and reason.
              {floor === null
                ? ''
                : ` It can’t be less than the ${floor} already awarded or waiting on this pool, this award included.`}
            </Notice>
            {open === p.pool.id && position !== undefined && year !== null ? (
              <div ref={editor}>
                <PoolEditor
                  pool={position}
                  fiscalYear={year}
                  onDone={() => {
                    done(p.pool.id);
                  }}
                />
              </div>
            ) : (
              <div className={s.actionsRow}>
                <Button
                  ref={(node) => {
                    if (node === null) setButtons.current.delete(p.pool.id);
                    else setButtons.current.set(p.pool.id, node);
                  }}
                  variant="navy"
                  isDisabled={position === undefined || year === null}
                  onPress={() => {
                    setOpen(p.pool.id);
                  }}
                >
                  Set the {name} budget
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function ExecutiveView({
  detail,
  canSetBudgets,
  onActed,
  onRefused,
}: {
  detail: ApprovalDetail;
  /** Executives set budgets (K34): a missing one is set in place before approving. */
  canSetBudgets: boolean;
  onActed: (done: ActedDone) => void;
  onRefused: (refusal: ActingRefusal) => void;
}): ReactElement {
  const { award, actions } = detail;
  // APP-148: a Stock Admin decides it only for a named Executive (Main board rows 2–4).
  const offer = actingOffer(detail);
  const acting =
    offer !== null && offer.role === 'executive' && (offer.actions.approve || offer.actions.reject)
      ? offer
      : null;
  const [step, setStep] = useState<ExecutiveStepOpen | null>(null);
  // Who may fill in the decision: whoever approves it, themselves or for an Executive.
  const approves = actions.approve || acting?.actions.approve === true;
  const legacyExecutive = detail.kind === 'self_recognition';
  const proofRequired = legacyExecutive && award.type === 'EDB';
  const [edits, setEdits] = useState<Edits>({});
  const [date, setDate] = useState(detail.decision.boardDecisionDate ?? '');
  const [note, setNote] = useState(detail.decision.note ?? '');
  const [drafts, setDrafts] = useTopUpDrafts(detail);
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const decision = useDecision(detail, (next) =>
    next.award.ended === 'rejected'
      ? `${award.displayId ?? 'The award'} was turned down`
      : `Approved · ${award.displayId ?? 'the award'} goes to the Stock Admin team`,
  );
  const dirty = isDirty(detail, edits);
  const over = overBudgetPools(detail);
  const notSet = detail.budget.filter((p) => p.status === 'not_set');
  const approveButton = useRef<HTMLButtonElement>(null);
  const decisionFields = useRef<HTMLDivElement>(null);
  // On a phone the side column comes after Approve: the top-up and the pools move up to sit just
  // before it, so the budget's verdict comes before the decision (X3 at 390, as R2 and SL4 do).
  const mainRef = useRef<HTMLDivElement>(null);
  const sideRef = useRef<HTMLDivElement>(null);
  const stacked = useStacked(mainRef, sideRef);
  const budgetCards = (
    <>
      {over.length > 0 && <TopUpCard detail={detail} drafts={drafts} onDrafts={setDrafts} />}
      <BudgetCard detail={detail} title="Pools this touches" />
    </>
  );
  const totals = editedTotals(detail, edits);
  const approveDisabled =
    dirty || notSet.length > 0 || totals.people === 0 || (proofRequired && !detail.proofReady);
  // A budget set in place: focus moves on once this approval's own data says the budget is set,
  // in the commit that enables Approve. Choosing earlier would find Approve still disabled.
  const focusOnceSet = useRef(false);
  useLayoutEffect(() => {
    if (!focusOnceSet.current || notSet.length > 0) return;
    focusOnceSet.current = false;
    if (!approveDisabled) approveButton.current?.focus();
    else decisionFields.current?.querySelector('input')?.focus();
  }, [notSet.length, approveDisabled]);
  const approve = (): void => {
    setProblem(null);
    if (over.length > 0) {
      const payload = topUpPayload(drafts);
      if (!payload.ok) {
        setProblem(payload.message);
        return;
      }
      if (acting !== null) setStep({ kind: 'approve', offer: acting });
      else setDialog('approve');
      return;
    }
    if (acting !== null) {
      setStep({ kind: 'approve', offer: acting });
      return;
    }
    decision.mutate({
      action: 'approve',
      version: award.version,
      ...(optional(note) !== undefined && { note: optional(note) }),
      ...(date !== '' && { boardDecisionDate: date }),
    });
  };
  return (
    <TwoColumns
      sideLabel="How it works and the pools"
      main={
        <div ref={mainRef} className={s.stack}>
          <SummaryCard
            detail={detail}
            status={{
              label: 'Approval · Executive decision',
              tone: 'waiting',
            }}
          >
            {award.writeUpText !== null && award.writeUpText.trim() !== '' && (
              <blockquote className={s.quote}>
                <span className={s.strong}>Write-up. </span>
                {award.writeUpText}
              </blockquote>
            )}
          </SummaryCard>
          <ReviewCard
            detail={detail}
            edits={edits}
            onEdits={setEdits}
            variant="executive"
            onRefused={onRefused}
          />
          {stacked && budgetCards}
          <Card
            title="Approve"
            helpAnchor="approval-approve"
            accent="cyan"
            help={{
              shows: legacyExecutive
                ? 'An Executive’s decision, recorded with their name and an optional note.'
                : 'The Board’s decision date and an optional note, recorded with your approval.',
              canDo: proofRequired
                ? 'Attach the Executives’ approval, then an Executive approves it. Any top-up is recorded in the same decision.'
                : 'Approve to send it to the Stock Admin team: the click is the record. Reject to turn it down.',
            }}
          >
            <div ref={decisionFields} className={s.formRow}>
              <TextField
                label="Board decision date"
                type="date"
                value={date}
                onChange={setDate}
                isDisabled={!approves}
              />
              <TextField
                label="Note (optional)"
                value={note}
                onChange={setNote}
                placeholder="For example: Board approved as entered"
                isDisabled={!approves}
              />
            </div>
            {approves && (
              <div className={s.attachRow}>
                <AttachButton
                  requestId={award.id}
                  purpose="proof"
                  label={proofRequired ? 'Attach required proof' : 'Attach proof (optional)'}
                />
                <span className={s.muted}>
                  {proofRequired
                    ? 'An email or a Teams screenshot. Required before approval.'
                    : 'Minutes or an email. Not required: the approval itself is the record.'}
                </span>
              </div>
            )}
            {proofRequired && !detail.proofReady && (
              <Notice tone="waiting">Attach the Executives’ approval first.</Notice>
            )}
            {dirty && (
              <Notice tone="waiting">Save your changes first, so you approve what you see.</Notice>
            )}
            {notSet.length > 0 &&
              (approves && canSetBudgets ? (
                <SetBudgetInPlace
                  detail={detail}
                  pools={notSet}
                  onSet={() => {
                    focusOnceSet.current = true;
                  }}
                />
              ) : (
                <Notice tone="waiting">
                  The {notSet.map((p) => p.pool.name).join(' and ')} budget isn’t set for this year.
                  The Executive who approves it sets it first.
                </Notice>
              ))}
            <div className={s.actionsRow}>
              {actions.approve && (
                <Button
                  ref={approveButton}
                  variant="cyan"
                  icon={<Icon name="check" size={18} />}
                  isDisabled={approveDisabled}
                  isPending={decision.isPending}
                  onPress={approve}
                >
                  {over.length > 0 ? 'Approve with top-up' : 'Approve — the click is the record'}
                </Button>
              )}
              {actions.reject && (
                <Button
                  variant="ghost"
                  onPress={() => {
                    setDialog('reject');
                  }}
                >
                  Reject
                </Button>
              )}
              {acting?.actions.approve === true && (
                <Button
                  ref={approveButton}
                  variant="cyan"
                  icon={<Icon name="check" size={18} />}
                  isDisabled={approveDisabled}
                  onPress={approve}
                >
                  Approve for an Executive…
                </Button>
              )}
              {acting?.actions.reject === true && (
                <Button
                  variant="ghost"
                  onPress={() => {
                    setStep({ kind: 'reject', offer: acting });
                  }}
                >
                  Reject for an Executive…
                </Button>
              )}
            </div>
            {acting !== null && <ActingNote />}
            {acting !== null && <OnAwardNote detail={detail} />}
            {problem !== null && <Notice tone="problem">{problem}</Notice>}
            {decision.error !== null && <Notice tone="problem">{decision.error}</Notice>}
          </Card>
          <ConfirmDialog
            isOpen={dialog === 'approve'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title="Approve with this top-up?"
            intro="This award goes over budget. The top-up goes in each pool’s ledger for the year, with your name; then the award goes to the Stock Admin team."
            summary={[
              {
                label: 'Award',
                value: `${award.displayId ?? ''} · ${includedWords(totals.people, totals.of)}`,
              },
              ...topUpSummary(drafts),
            ]}
            reasonLabel="Reason for the top-up (optional)"
            reasonRequired={false}
            confirmLabel="Approve with top-up"
            onConfirm={async (reason) => {
              const payload = topUpPayload(drafts);
              if (!payload.ok) return;
              const text = [note.trim(), reason.trim()].filter(Boolean).join(' · ');
              await decision
                .mutateAsync({
                  action: 'approve',
                  version: award.version,
                  topUps: payload.topUps,
                  ...(text !== '' && { note: text }),
                  ...(date !== '' && { boardDecisionDate: date }),
                })
                .catch(() => undefined);
            }}
          />
          {step !== null && (
            <ExecutiveStep
              detail={detail}
              drafts={over.length > 0 ? drafts : []}
              decision={{ date, note }}
              step={step}
              onActed={onActed}
              onRefused={onRefused}
              onClose={() => {
                setStep(null);
              }}
            />
          )}
          <ConfirmDialog
            isOpen={dialog === 'reject'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={`Turn down ${award.displayId ?? 'this award'}?`}
            intro="It won’t be paid, and whoever entered it is told."
            summary={[{ label: 'Award', value: award.displayId ?? 'This award' }]}
            reasonLabel="Reason (optional)"
            reasonRequired={false}
            confirmLabel="Reject"
            onConfirm={async (reason) => {
              await decision
                .mutateAsync({
                  action: 'reject',
                  version: award.version,
                  ...(optional(reason) !== undefined && { reason: optional(reason) }),
                })
                .catch(() => undefined);
            }}
          />
        </div>
      }
      side={
        <div ref={sideRef} className={s.stack}>
          <HowItWorksCard
            steps={[
              legacyExecutive
                ? 'Any one Executive can approve this award.'
                : 'The Executives, the President and the Board decide outside the app.',
              'The approving Executive edits people and amounts here and approves.',
              proofRequired
                ? 'The Executives’ approval must be attached before this EDB can be approved.'
                : 'The click is the record. Proof is optional.',
              'The Stock Admin team does one check (they can still move people between pools, logged), then the letters go out.',
              award.type === 'TAA'
                ? 'Payroll pays and attaches the earning statement, then the award closes. A TAA is cash only: no election.'
                : award.type === 'PAA'
                  ? 'The person chooses cash, stock or a mix; Payroll pays, then the award closes.'
                  : 'Payroll pays the cash; options go into Shareworks, then the award closes.',
            ]}
          />
          {!stacked && budgetCards}
          <ChargeRuleCard />
          <AlsoWaiting currentId={award.id} forExecutives={acting !== null} />
        </div>
      }
    />
  );
}

// --- R3: record an Executive Discretionary decision --------------------------------------------------

function ProofDrop({ requestId }: { requestId: string }): ReactElement {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { refetch } = useQuery(documentsQuery(requestId));
  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { document } = await uploadDocument(requestId, { file, purpose: 'proof' });
      toast.show({
        tone: 'done',
        title: `${document.fileName} attached`,
        body: 'It counts as proof once the virus check clears it.',
      });
      await refetch();
    } catch (e) {
      setError(problemWords(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <DropZone
        className={s.drop}
        aria-label="Drop the Executives’ approval here"
        onDrop={(event) => {
          const item = event.items.find((i) => i.kind === 'file');
          if (item?.kind === 'file') void item.getFile().then(upload);
        }}
      >
        <Icon name="download" size={24} />
        <span className={s.dropText}>
          <span className={s.strong}>Drop the Executives’ approval here</span>
          <span className={s.muted}>
            An email (.msg or .eml) or a Teams screenshot (.png, .jpg)
          </span>
        </span>
        <FileTrigger
          acceptedFileTypes={ACCEPTED_FILES}
          onSelect={(files) => {
            const file = files?.[0];
            if (file) void upload(file);
          }}
        >
          <Button variant="ghost" size="sm" isPending={busy}>
            Choose a file
          </Button>
        </FileTrigger>
      </DropZone>
      {error !== null && <Notice tone="problem">{error}</Notice>}
    </>
  );
}

function RecordView({
  detail,
  documents,
  onActed,
  onRefused,
}: {
  detail: ApprovalDetail;
  documents: readonly DocumentView[];
  onActed: (done: ActedDone) => void;
  onRefused: (refusal: ActingRefusal) => void;
}): ReactElement {
  const { award, actions } = detail;
  // APP-148: a Stock Admin records it on their own authority, through the step, naming the
  // Executive from today's list (Main board row 6). Not `actingOffer`: the recording is theirs.
  const forExecutive =
    detail.actingFor?.role === 'executive' && detail.actingFor.actions.recordDecision
      ? detail.actingFor
      : null;
  // The step keeps the offer it opened with, through a refusal and the read after it (review
  // focus 3), until it's confirmed or cancelled.
  const [step, setStep] = useState<ApprovalOffer | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [date, setDate] = useState(detail.decision.boardDecisionDate ?? '');
  const [executive, setExecutive] = useState<string>(detail.decision.recordedExecutive?.id ?? '');
  const [dialog, setDialog] = useState<'reject' | null>(null);
  const over = overBudgetPools(detail);
  const budgetVisible = award.calculation.budgetVisible !== false;
  const decision = useDecision(detail, (next) =>
    next.award.ended === 'rejected'
      ? `${award.displayId ?? 'The request'} was turned down`
      : // A delegate reads "executives" where others read over budget (APP-204, SENT-259).
        next.award.waitingOn === 'over_budget' || next.award.waitingOn === 'executives'
        ? `Recorded · ${award.displayId ?? 'the request'} goes to the Executives${budgetVisible ? ' for a top-up' : ''}`
        : `Recorded · ${award.displayId ?? 'the request'} goes to the Stock Admin team`,
  );
  const dirty = isDirty(detail, edits);
  const totals = editedTotals(detail, edits);
  const requested = detail.lines.reduce((sum, l) => sum + l.cashCents, 0);
  const proofs = documents.filter((d) => d.purpose === 'proof');
  const pending = proofs.some((d) => d.scanStatus === 'pending');
  const pool = detail.budget[0];
  return (
    <TwoColumns
      sideLabel="Summary and decision"
      main={
        <>
          <ReviewCard detail={detail} edits={edits} onEdits={setEdits} variant="record" />
          <Card
            title="Who approved it"
            // Recording it for an Executive names one, and must (R66): nothing here is optional.
            help={
              forExecutive !== null
                ? {
                    shows:
                      'The Executive who approved it outside the app, and the date. They go on the record.',
                    canDo:
                      'Add the date here. You name the Executive in the step, from today’s list.',
                  }
                : {
                    shows:
                      'The Executive who approved it outside the app, and the date. Both are optional and go on the record.',
                    canDo:
                      'Choose the Executive and the date, if you know them. Choosing someone gives them nothing to do.',
                  }
            }
            {...(forExecutive === null && {
              aside: <span className={s.muted}>Optional</span>,
            })}
          >
            {forExecutive !== null ? (
              <p className={s.muted}>
                You name the Executive who approved it when you record it, from today’s list of
                Executives.
              </p>
            ) : detail.executiveChoices.length === 0 ? (
              <p className={s.muted}>No Executives are listed yet.</p>
            ) : (
              <RadioGroup
                aria-label="Who approved it"
                value={executive}
                onChange={setExecutive}
                orientation="horizontal"
                isDisabled={!actions.recordDecision}
                className={s.chipGroup}
              >
                {detail.executiveChoices.map((choice) => (
                  <RadioField key={choice.id} value={choice.id}>
                    <RadioButton className={s.chipRadio}>{choice.name}</RadioButton>
                  </RadioField>
                ))}
              </RadioGroup>
            )}
            <div className={s.formRow}>
              <TextField
                label="Date approved"
                type="date"
                value={date}
                onChange={setDate}
                isDisabled={!actions.recordDecision}
              />
            </div>
          </Card>
          <Card
            title="Proof"
            help={{
              shows:
                'The Executives’ approval: an email or a Teams screenshot. It stays private to the staff who handle the award.',
              canDo:
                'Attach it, replace it or remove it. It counts as proof once the virus check clears it.',
            }}
            aside={
              <StatusPill
                tone={detail.proofReady ? 'done' : 'waiting'}
                size="sm"
                label={detail.proofReady ? 'Proof ready' : 'Required'}
              />
            }
          >
            {actions.recordDecision && <ProofDrop requestId={award.id} />}
            <FileList
              requestId={award.id}
              documents={proofs}
              canChange={actions.recordDecision}
              empty={<p className={s.muted}>No proof attached yet.</p>}
            />
            {pending && (
              <Notice tone="waiting">
                Checking the file for viruses. It counts as proof as soon as it’s cleared, usually
                within a minute.
              </Notice>
            )}
          </Card>
        </>
      }
      side={
        <>
          <Card
            title="Summary"
            accent="cyan"
            help={{
              shows: budgetVisible
                ? 'Who’s approved, the total, and what it leaves in the pool.'
                : 'Who’s approved and the total award amount.',
              canDo: 'When the proof is ready, send it on. Stock Admins can do this step too.',
            }}
          >
            <dl className={s.summary}>
              <div className={s.summaryRow}>
                <dt>People approved</dt>
                <dd className={s.strong}>
                  {totals.people} of {totals.of}
                </dd>
              </div>
              <div className={s.summaryRow}>
                <dt>Total cash</dt>
                <dd className={s.strong}>{formatMoney(totals.cashCents)}</dd>
              </div>
              <div className={s.summaryRow}>
                <dt>On the request</dt>
                <dd>{formatMoney(requested)}</dd>
              </div>
            </dl>
            {pool !== undefined && pool.remainingAfter !== null && (
              <div className={s.poolAfter}>
                <p className={cx('eyebrow', s.fieldLabel)}>{scoped(pool.pool.name, 'cash left')}</p>
                <p className={s.poolMove}>
                  {formatMoney(
                    pool.remainingAfter.cashCents + demandOf(detail, pool.pool.id).cashCents,
                  )}
                  <ThenArrow />
                  <span
                    className={cx(
                      'disp',
                      s.bigValue,
                      pool.remainingAfter.cashCents < 0 && s.negative,
                    )}
                  >
                    {formatMoney(pool.remainingAfter.cashCents)}
                  </span>
                </p>
              </div>
            )}
            {over.length > 0 && (
              <Notice tone="waiting">
                It goes over budget. Recording it sends it to the Executives to approve a top-up
                first.
              </Notice>
            )}
            {!detail.proofReady && (
              <Notice tone="waiting">Attach the Executives’ approval first.</Notice>
            )}
            {dirty && (
              <Notice tone="waiting">Save your changes first, so you send what you see.</Notice>
            )}
            {actions.recordDecision && forExecutive !== null && (
              <>
                <Button
                  variant="cyan"
                  block
                  isDisabled={!detail.proofReady || dirty || totals.people === 0}
                  onPress={() => {
                    setStep(forExecutive);
                  }}
                >
                  Record for an Executive…
                </Button>
                <ActingNote
                  lead="You’d be recording it for an Executive."
                  next="Next you’ll pick the Executive who approved it, and you can add files or a note."
                />
                <OnAwardNote detail={detail} />
              </>
            )}
            {actions.recordDecision && forExecutive === null && (
              <Button
                variant="cyan"
                block
                isDisabled={!detail.proofReady || dirty || totals.people === 0}
                isPending={decision.isPending}
                onPress={() => {
                  decision.mutate({
                    action: 'record_decision',
                    version: award.version,
                    ...(date !== '' && { boardDecisionDate: date }),
                    ...(executive !== '' && { recordedExecutiveId: executive }),
                  });
                }}
              >
                {!budgetVisible
                  ? 'Record decision and send'
                  : over.length > 0
                    ? 'Send to the Executives'
                    : 'Send to the Stock Admin team'}
              </Button>
            )}
            {actions.reject && (
              <Button
                variant="ghost"
                block
                onPress={() => {
                  setDialog('reject');
                }}
              >
                Not approved · turn it down
              </Button>
            )}
            <p className={s.muted}>
              {budgetVisible
                ? 'Next: the Stock Admin team’s one check, then the letters.'
                : 'The award follows any required approvals before the Stock Admin team’s check.'}
            </p>
            {decision.error !== null && <Notice tone="problem">{decision.error}</Notice>}
          </Card>
          <AlsoWaiting currentId={award.id} />
          {step !== null && (
            <RecordStep
              detail={detail}
              offer={step}
              date={date}
              onActed={onActed}
              onRefused={onRefused}
              onClose={() => {
                setStep(null);
              }}
            />
          )}
          <ConfirmDialog
            isOpen={dialog === 'reject'}
            onOpenChange={(open) => {
              if (!open) setDialog(null);
            }}
            title={`Turn down ${award.displayId ?? 'this request'}?`}
            intro="The Executives didn’t approve it. It won’t be paid, and whoever entered it is told."
            summary={[{ label: 'Request', value: award.displayId ?? 'This request' }]}
            reasonLabel="Reason (optional)"
            reasonRequired={false}
            confirmLabel="Turn it down"
            onConfirm={async (reason) => {
              await decision
                .mutateAsync({
                  action: 'reject',
                  version: award.version,
                  ...(optional(reason) !== undefined && { reason: optional(reason) }),
                })
                .catch(() => undefined);
            }}
          />
        </>
      }
    />
  );
}

// --- Already decided ---------------------------------------------------------------------------------

function DecidedView({ detail }: { detail: ApprovalDetail }): ReactElement {
  const { award, decision } = detail;
  const rows: [string, string][] = [
    ['Approved by', decision.approvedBy?.name ?? 'Not approved'],
    ['When', decision.approvedAt === null ? 'Not approved' : formatDate(decision.approvedAt)],
    [
      'Board or Executive decision',
      decision.boardDecisionDate === null
        ? 'No date recorded'
        : formatDate(decision.boardDecisionDate),
    ],
    ['Who approved it outside the app', decision.recordedExecutive?.name ?? 'Not named'],
    ['Note', decision.note ?? 'No note'],
  ];
  return (
    <TwoColumns
      sideLabel="More approvals"
      main={
        <Card
          title="Decided"
          help={{
            shows: 'This award has left approval. Here’s what was recorded about the decision.',
            canDo: 'Open the full record for its stage and history.',
          }}
          aside={
            <StatusPill
              tone={award.ended ? 'problem' : 'done'}
              label={award.ended ? 'Turned down' : 'Decided'}
            />
          }
        >
          <dl className={s.summary}>
            {rows.map(([label, value]) => (
              <div key={label} className={s.summaryRow}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <div className={s.actionsRow}>
            <ButtonLink href={`/awards/${award.id}`} variant="navy" size="sm">
              Open the full record
            </ButtonLink>
            <ButtonLink href="/approvals" variant="ghost" size="sm">
              All approvals
            </ButtonLink>
          </div>
        </Card>
      }
      side={<AlsoWaiting currentId={award.id} />}
    />
  );
}

/** SL7, X2, X3 and R3: one decision screen, laid out by what the decision is. */
export function ApprovalPage(): ReactElement {
  const { id = '' } = useParams();
  const { data: detail, dataUpdatedAt } = useSuspenseQuery(approvalQuery(id));
  const { data: me } = useSuspenseQuery(meQuery);
  // What a Stock Admin just did for an Executive here (APP-148, Approved board). The award has
  // moved on, so this page says what was done instead of how it's decided.
  const [acted, setActed] = useState<ActedDone | null>(null);
  // A step refused because the page moved on (someone decided first): the step went with the
  // page, so the page that took its place says why nothing was saved. Only this visit's, and only
  // when the page moved on under it (R57).
  const [refusal, setRefusal] = useState<HeldRefusal | null>(null);
  // A Sector Lead's step open on this award, with the lead it opened with: the page keeps it,
  // through a refusal and the read after it, until it's closed (review focus 3).
  const [leadStep, setLeadStep] = useState<{ awardId: string; offer: LeadOffer } | null>(null);
  // The step goes with its page: another award, or this one moved on (decided, here or by
  // someone first), clears it.
  if (leadStep !== null && (leadStep.awardId !== detail.award.id || detail.kind !== 'delegate'))
    setLeadStep(null);
  const leadOpened = leadStep?.awardId === detail.award.id ? leadStep.offer : null;
  // R69: while that step stays open after a switch-off refusal, the page reads as it opened, the
  // acting page, until the step closes.
  const shown =
    leadOpened !== null && actingOffer(detail) === null
      ? { ...detail, actingFor: leadOpened }
      : detail;
  if (refusal !== null) {
    const settled = settleRefusal(refusal, detail, dataUpdatedAt);
    if (settled !== refusal) setRefusal(settled);
  }
  const queryClient = useQueryClient();
  const onRefused = (words: ActingRefusal): void => {
    const read = queryClient.getQueryState(approvalQuery(id).queryKey)?.dataUpdatedAt ?? 0;
    setRefusal({ ...words, at: read, movedOn: false });
  };
  const { data: docs } = useQuery({
    ...documentsQuery(id),
    refetchInterval: (query) =>
      query.state.data?.documents.some((d) => d.scanStatus === 'pending') === true ? 5_000 : false,
  });
  // A proof that has just cleared its virus check changes what the server says is ready.
  useQuery({
    ...approvalQuery(id),
    refetchInterval:
      (detail.kind === 'record_decision' ||
        (detail.kind === 'self_recognition' && detail.award.type === 'EDB')) &&
      !detail.proofReady
        ? 5_000
        : false,
  });
  const documents = docs?.documents ?? [];
  const people = detail.lines.filter((l) => l.included);
  const cash = people.reduce((sum, l) => sum + l.cashCents, 0);
  const over = overBudgetPools(detail);
  const firstOver = over[0];
  const id_ = detail.award.displayId ?? 'This award';
  const year = detail.award.fiscalYear ?? null;
  // SL7: a delegate's entry is decided against the sector's pool, so the hero is that pool as it
  // stands before this entry, the same number Budget impact starts from.
  // A Stock Admin deciding it for the lead sees the entry's total, as the LeadApprove board does,
  // and for the Executive it was entered for (APP-274).
  const forLead = detail.kind === 'delegate' && actingOffer(shown) !== null;
  const leadCheck = detail.kind === 'delegate' && !forLead ? sectorPool(detail) : undefined;
  const { data: positions } = useQuery({
    ...budgetsForYearQuery(year ?? 0),
    enabled: year !== null && leadCheck !== undefined,
    retry: false,
  });
  const stats =
    firstOver !== undefined && detail.kind === 'overage'
      ? [
          {
            label: scoped(
              `${firstOver.pool.name} pool`,
              year === null ? 'cash left this year' : `cash left ${String(year)}`,
            ),
            value: formatMoney(
              (firstOver.remainingAfter?.cashCents ?? 0) +
                demandOf(detail, firstOver.pool.id).cashCents,
            ),
            meta: `This award: ${formatMoney(demandOf(detail, firstOver.pool.id).cashCents)} · ${includedWords(people.length, people.length)}`,
          },
        ]
      : leadCheck !== undefined && year !== null
        ? [
            poolStat({
              name: leadCheck.pool.name,
              fiscalYear: year,
              left:
                leadCheck.status === 'not_set' || leadCheck.remainingAfter === null
                  ? null
                  : {
                      cashCents:
                        leadCheck.remainingAfter.cashCents +
                        demandOf(detail, leadCheck.pool.id).cashCents,
                      options:
                        leadCheck.remainingAfter.options +
                        demandOf(detail, leadCheck.pool.id).options,
                    },
              budgetCents:
                positions?.pools.find((p) => p.pool.id === leadCheck.pool.id)?.budget?.cashCents ??
                null,
            }),
          ]
        : [
            {
              label: scoped(id_, 'total cash'),
              value: formatMoney(cash),
              meta: `${includedWords(people.length, detail.lines.length)}${detail.kind === null ? '' : ` · ${forLead ? 'Delegate’s entry' : KIND_LABEL[detail.kind]}`}`,
            },
          ];
  if (acted !== null && acted.awardId === detail.award.id)
    return <ActedConfirmation done={acted} detail={detail} me={me} />;
  const heading =
    actingOffer(shown) === null
      ? approvalHeading(detail)
      : actingHeading(shown, approvalHeading(detail));
  const movedOn = refusal?.movedOn === true ? refusal : null;
  // The lead's entry, decided for them: while they're offered, or while the step stays open.
  const leadOffer = (forLead ? detail.actingFor : null) ?? leadOpened;
  return (
    <>
      <Hero
        eyebrow={heading.eyebrow}
        eyebrowTone={
          detail.kind === 'overage' || detail.kind === 'record_decision' ? 'gold' : 'cyan'
        }
        title={heading.title}
        sub={heading.sub}
        stats={stats}
        titleTestId="approval-title"
      />
      {/* A Stock Admin's kind switch is for the Executives' decisions; not on a lead's entry. */}
      {(me.roles.some((r) => r.role === 'executive') || filterOf(detail.kind) !== null) && (
        <ExecutiveBar me={me} current={filterOf(detail.kind)} />
      )}
      <PageBody>
        <div className={s.stack}>
          <BackLink href="/approvals">Approvals</BackLink>
          {movedOn !== null && <Notice tone="problem">Nothing was saved. {movedOn.words}</Notice>}
          {detail.kind === 'delegate' &&
            (leadOffer != null ? (
              <LeadEntryView
                detail={detail}
                offer={leadOffer}
                documents={documents}
                onOpenChange={(opened) => {
                  setLeadStep(opened === null ? null : { awardId: detail.award.id, offer: opened });
                }}
                onActed={(done) => {
                  // Done: the step has closed with the page it was on.
                  setLeadStep(null);
                  setActed(done);
                }}
                onRefused={onRefused}
              />
            ) : (
              <DelegateView detail={detail} documents={documents} />
            ))}
          {detail.kind === 'overage' && (
            <OverageView detail={detail} onActed={setActed} onRefused={onRefused} />
          )}
          {(detail.kind === 'executive' || detail.kind === 'self_recognition') && (
            <ExecutiveView
              detail={detail}
              // Executives set budgets (K34); so does a Stock Admin approving for one.
              canSetBudgets={
                me.roles.some((r) => r.role === 'executive') ||
                detail.actingFor?.role === 'executive'
              }
              onActed={setActed}
              onRefused={onRefused}
            />
          )}
          {detail.kind === 'record_decision' && (
            <RecordView
              detail={detail}
              documents={documents}
              onActed={setActed}
              onRefused={onRefused}
            />
          )}
          {detail.kind === null && <DecidedView detail={detail} />}
        </div>
      </PageBody>
    </>
  );
}
